import { Modal, Form, Select, Alert, Card, Row, Col, Typography } from 'antd';
import type { FormInstance } from 'antd';
import {
    ExclamationCircleOutlined, UserDeleteOutlined, UserOutlined,
    DollarOutlined, CheckCircleOutlined, IdcardOutlined, StopOutlined,
} from '@ant-design/icons';
import { BRAND } from '../constants';
import { formatOutstandingAmount } from '../utils';

const { Option } = Select;
const { Text, Title, Paragraph } = Typography;

export interface SuspendSuccessInfo {
    name: string;
    staff_id: string;
    outstandingAmount?: number;
}

interface SuspendModalProps {
    visible: boolean;
    view: 'form' | 'success';
    suspending: boolean;
    loadingReasons: boolean;
    suspensionReasons: any[];
    successInfo: SuspendSuccessInfo | null;
    form: FormInstance;
    onOk: () => void;
    onCancel: () => void;
    onFinish: (values?: any) => void;
}

export default function SuspendModal({
    visible, view, suspending, loadingReasons, suspensionReasons,
    successInfo, form, onOk, onCancel, onFinish,
}: SuspendModalProps) {
    const successBody = successInfo ? (
        <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', width: 96, height: 96, margin: '0 auto 20px' }}>
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(26, 122, 126, 0.20) 0%, rgba(13, 82, 87, 0.08) 100%)',
                        animation: 'suspendSuccessPulse 2s ease-in-out infinite',
                    }}
                />
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: BRAND.heroGradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 10px 28px rgba(13, 82, 87, 0.4)',
                    }}
                >
                    <CheckCircleOutlined style={{ fontSize: 48, color: '#fff' }} />
                </div>
            </div>

            <Title level={3} style={{ margin: '0 0 6px', color: '#262626', fontWeight: 700 }}>
                User Suspended
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 28, fontSize: 14 }}>
                Access to the platform has been restricted for this employee
            </Text>

            <div
                style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: '20px 24px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    marginBottom: 16,
                    textAlign: 'left',
                    border: '1px solid #f0f0f0',
                }}
            >
                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                padding: '14px 16px',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)',
                                borderRadius: 10,
                                border: '1px solid #e9ecef',
                                height: '100%',
                            }}
                        >
                            <div
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    background: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
                                }}
                            >
                                <UserOutlined style={{ fontSize: 20, color: BRAND.teal }} />
                            </div>
                            <div>
                                <Text style={{ fontSize: 11, color: BRAND.teal, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Employee
                                </Text>
                                <div style={{ fontSize: 16, fontWeight: 600, color: '#212529', marginTop: 2 }}>
                                    {successInfo.name}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={12}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                padding: '14px 16px',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)',
                                borderRadius: 10,
                                border: '1px solid #e9ecef',
                                height: '100%',
                            }}
                        >
                            <div
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    background: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
                                }}
                            >
                                <IdcardOutlined style={{ fontSize: 20, color: BRAND.teal }} />
                            </div>
                            <div>
                                <Text style={{ fontSize: 11, color: BRAND.teal, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Staff ID
                                </Text>
                                <div style={{ fontSize: 16, fontWeight: 600, color: '#212529', marginTop: 2 }}>
                                    {successInfo.staff_id}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            <Card
                style={{
                    background: BRAND.heroGradient,
                    border: 'none',
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(13, 82, 87, 0.28)',
                    marginBottom: 20,
                }}
                bodyStyle={{ padding: '20px 24px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div style={{ textAlign: 'left' }}>
                        <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', fontWeight: 500, display: 'block', marginBottom: 6 }}>
                            Outstanding Amount
                        </Text>
                        <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                            {formatOutstandingAmount(successInfo.outstandingAmount)}
                        </div>
                    </div>
                    <div
                        style={{
                            width: 52,
                            height: 52,
                            borderRadius: 12,
                            background: 'rgba(255, 255, 255, 0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <DollarOutlined style={{ fontSize: 26, color: '#fff' }} />
                    </div>
                </div>
            </Card>

            <div
                style={{
                    padding: '16px 20px',
                    background: BRAND.tealSoft,
                    borderRadius: 10,
                    border: '1px solid #cfe7e7',
                    textAlign: 'left',
                }}
            >
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <StopOutlined style={{ fontSize: 18, color: BRAND.tealDark, marginTop: 2 }} />
                    <Paragraph style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: '#4a5b5e' }}>
                        User <Text strong style={{ color: '#262626' }}>{successInfo.name}</Text> with staff ID{' '}
                        <Text strong style={{ color: '#262626' }}>{successInfo.staff_id}</Text> is now suspended with an outstanding amount of:{' '}
                        <Text strong style={{ color: BRAND.tealDark }}>
                            {formatOutstandingAmount(successInfo.outstandingAmount)}
                        </Text>
                    </Paragraph>
                </div>
            </div>

            <style>{`
                @keyframes suspendSuccessPulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.12); opacity: 0.6; }
                }
            `}</style>
        </div>
    ) : null;

    const formBody = (
        <>
            <Alert
                message="Warning"
                description="You are about to suspend this user. This action will restrict their access to the platform."
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{
                    marginBottom: '18px',
                    borderRadius: '10px',
                    border: '1px solid #ffe08a',
                }}
            />
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={{
                    background: '#fff',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #EEF3F3',
                }}
            >
                <Form.Item
                    label={
                        <span style={{ fontSize: '15px', fontWeight: 600, color: '#262626' }}>
                            Reason <span style={{ color: '#ff4d4f' }}>*</span>
                        </span>
                    }
                    name="reasonID"
                    required={false}
                    rules={[{ required: true, message: 'Please select a reason!' }]}
                    style={{ marginBottom: '0' }}
                >
                    <Select
                        placeholder="Select a reason for suspension"
                        loading={loadingReasons}
                        size="large"
                        style={{ borderRadius: '8px' }}
                    >
                        {suspensionReasons.map((reason: any) => (
                            <Option key={reason.id} value={reason.id}>
                                {reason.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </>
    );

    const titleNode = view === 'success' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '24px', color: BRAND.tealDark }} />
            <Title level={4} style={{ margin: 0, color: '#262626' }}>
                Suspension Complete
            </Title>
        </div>
    ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
            <UserDeleteOutlined style={{ fontSize: '24px', color: BRAND.tealDark }} />
            <Title level={4} style={{ margin: 0, color: '#262626' }}>
                Suspend User
            </Title>
        </div>
    );

    return (
        <Modal
            title={titleNode}
            visible={visible}
            onCancel={onCancel}
            onOk={onOk}
            width={620}
            centered
            zIndex={1010}
            wrapClassName="np-suspend-modal"
            okText={view === 'success' ? 'OK' : 'Suspend'}
            cancelText="Cancel"
            confirmLoading={view === 'form' && suspending}
            cancelButtonProps={
                view === 'success'
                    ? { style: { display: 'none' } }
                    : {
                        style: {
                            borderRadius: '8px',
                            height: '42px',
                            padding: '0 32px',
                            border: '2px solid #e8e8e8',
                        },
                    }
            }
            okButtonProps={{
                disabled: view === 'form' && loadingReasons,
                style: {
                    background: BRAND.tealDark,
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    height: '42px',
                    padding: '0 32px',
                    boxShadow: '0 4px 15px rgba(13, 82, 87, 0.3)',
                },
            }}
            bodyStyle={{ padding: '32px', background: '#fafafa' }}
        >
            {view === 'success' ? successBody : formBody}
        </Modal>
    );
}
